import React, { useEffect, useState } from 'react';

import {
  Alert, Skeleton, Stack,
} from '@mui/material';
import _ from 'lodash';

import AdditionModal from '../../../../../components/AdditionModal';
import Button from '../../../../../components/Button';
import ContentCard from '../../../../../components/ContentCard';
import ContentGroupsList from '../../../../../components/ContentGroupsList';
import { useGetLessonTemplatesQuery } from '../../../../../services/lessons';
import { ShortLessonTemplate } from '../../../../../services/lessons/types';
import { useUpdateStreamMutation } from '../../../../../services/streams';
import { Stream } from '../../../../../services/streams/types';
import useError from '../../../../../utils/react/hooks/useError';

type Lesson = {
  id: string,
  lessonId: string,
  label: string,
}

interface LessonTemplatesCardProps {
  stream?: Stream,
  onLessonClick?: (lessonId: string) => void,
}

const LessonTemplatesCard = ({ stream, onLessonClick }: LessonTemplatesCardProps) => {
  const [orderedLessons, setOrderedLessons] = useState<Lesson[]>([]);
  const [addLessonTemplateSearchStr, setAddLessonTemplateSearchStr] = useState('');
  const [addLessonTemplateFormOpen, setAddLessonTemplateFormOpen] = useState<boolean>(false);

  const [updateStream, {
    isSuccess: isUpdateStreamSuccess,
    isLoading: isUpdateStreamLoading,
    isError: isUpdateStreamError,
    error: updateStreamError,
    reset: resetUpdateStream,
  }] = useUpdateStreamMutation();
  const { data: lessonTemplatesData } = useGetLessonTemplatesQuery(
    { course: stream?.course }, { skip: !stream },
  );

  useEffect(() => {
    if (stream?.orderedLessons && lessonTemplatesData?.data) {
      const newOrderedLessons = stream?.orderedLessons.map((lessonId) => {
        const lessonTemplate = lessonTemplatesData?.data
          .find((template) => template.lessonId === lessonId);

        return ({
          // Random string is attached to account for duplicate lesson templates
          // (eg. reivsion lessons) in the ordered lessons list
          // The random string consists of 4 alphanumeric characters
          id: `${lessonId}#${Math.random().toString(36).slice(2, 6)}`,
          lessonId,
          label: lessonTemplate?.combinedLabel || 'Unknown',
        });
      }) || [];
      setOrderedLessons(newOrderedLessons);
    }
  }, [stream?.orderedLessons, lessonTemplatesData?.data]);

  useError(isUpdateStreamError, updateStreamError);

  if (!stream || !lessonTemplatesData) {
    return (
      <Skeleton variant="rectangular" height={128} />
    );
  }

  return (
    <ContentCard variant="outlined" title="Ordered Lesson Templates">
      <Stack spacing={2}>
        <Alert severity="warning">
          Adding or re-ordering lessons for a stream affects
          future classes only. <b>Existing classes are NOT affected.</b>
        </Alert>
        <ContentGroupsList
          columns={[
            {
              field: 'lessonId',
              headerName: 'ID',
              width: 80,
            },
            {
              field: 'label',
              headerName: 'Label',
              width: 200,
            },
            {
              field: 'control',
              headerName: 'Control',
              renderCell: ({ row }: any) => (
                <Button
                  color="error"
                  size="small"
                  variant="text"
                  onClick={async (e) => {
                    e.preventDefault();
                    e.stopPropagation();

                    await updateStream({
                      course: stream.course,
                      streamId: stream.streamId,
                      newAttributes: {
                        // We filter orderedLessons instead of the stream.orderedLessons
                        // array to ensure the correct lesson is removed in the presence
                        // of duplicates
                        orderedLessons: orderedLessons.filter(
                          (lesson) => lesson.id !== row.id,
                        ).map((x) => x.lessonId),
                      },
                    });
                  }}
                  disabled={isUpdateStreamLoading}
                >
                  Remove
                </Button>
              ),
              width: 120,
            },
          ]}
          actions={{
            reorder: {
              fn: async (fields) => {
                const newOrderedLessons = fields?.orderedData.map((x: Lesson) => x.lessonId);
                if (_.xor(newOrderedLessons, stream.orderedLessons).length === 0) {
                  updateStream({
                    course: stream.course,
                    streamId: stream.streamId,
                    newAttributes: {
                      orderedLessons: newOrderedLessons,
                    },
                  });
                }
              },
              label: 'Reorder',
              isSuccess: isUpdateStreamSuccess,
              isLoading: isUpdateStreamLoading,
              error: updateStreamError,
            },
            create: {
              fn: () => setAddLessonTemplateFormOpen(true),
              label: 'Add',
            },
          }}
          data={orderedLessons}
          loading={!stream?.orderedLessons}
          onRowClick={({ row }) => onLessonClick && onLessonClick(row.lessonId)}
        />
      </Stack>
      <AdditionModal
        title="Add Lesson Template"
        searchPlaceholder="Template"
        open={addLessonTemplateFormOpen}
        handleClose={() => {
          setAddLessonTemplateFormOpen(false);
        }}
        onAdd={async (x: ShortLessonTemplate) => updateStream({
          course: stream.course,
          streamId: stream.streamId,
          newAttributes: {
            orderedLessons: [...stream.orderedLessons, x.lessonId],
          },
        })}
        isLoading={isUpdateStreamLoading}
        isSuccess={isUpdateStreamSuccess}
        isError={isUpdateStreamError}
        error={(updateStreamError as any)?.data?.message}
        closeNotifs={resetUpdateStream}
        searchFieldProps={{
          options: (lessonTemplatesData?.data || []).filter(
            (p) => p.combinedLabel.toLowerCase().includes(addLessonTemplateSearchStr.toLowerCase()),
          ),
          getOptionLabel: (option: ShortLessonTemplate) => option.combinedLabel,
          getOptionKey: (option: ShortLessonTemplate) => option.id,
          onSearch: (s) => setAddLessonTemplateSearchStr(s),
        }}
      />
    </ContentCard>
  );
};

export default LessonTemplatesCard;
